import queryString from 'query-string';
import { either, isEmpty, isNil } from 'ramda';

import type { GUID } from './auth';
import { isValidGuid } from './auth';

export type Params = Record<string, string | undefined>;
export type RoutingContext = { params: Params; path: string };
export type RouteCallback = ({ params, path }: RoutingContext) => void;
export type RouteDefinition =
  | RouteCallback
  | {
      start: RouteCallback;
      exit: RouteCallback;
    };

const isCallback = (route: RouteDefinition): route is RouteCallback => {
  return typeof route === 'function';
};

export type IProvidePathName = {
  pathname: () => string;
};

export const windowLocationPathProvider: IProvidePathName = {
  pathname() {
    return window.location.pathname;
  }
};

export type RoutingEffect = {
  initialize: (
    pathnameProvider: IProvidePathName,
    routes: Record<string, RouteDefinition>
  ) => void;
  replaceUrl: (url: string) => void;
  goTo: ({
    url,
    params,
    query
  }: {
    url: string;
    params?: Params;
    query?: Params;
  }) => void;
  tenantId: GUID | undefined;
};

type CompiledRoute = {
  pattern: string;
  paramNames: string[];
  regex: RegExp;
  definition: RouteDefinition;
};

function compileRoute(pattern: string): { paramNames: string[]; regex: RegExp } {
  const paramNames: string[] = [];
  const regexStr = pattern.replace(/:([^/]+)/g, (_match, paramName) => {
    paramNames.push(paramName);
    return '([^/]+)';
  });
  return { paramNames, regex: new RegExp(`^${regexStr}$`) };
}

function matchRoute(
  path: string,
  compiledRoutes: CompiledRoute[]
): { route: CompiledRoute; params: Params } | undefined {
  for (const route of compiledRoutes) {
    const match = path.match(route.regex);
    if (match) {
      const params: Params = {};
      route.paramNames.forEach((name, i) => {
        params[name] = decodeURIComponent(match[i + 1]);
      });
      return { route, params };
    }
  }
  return undefined;
}

let _routes: Record<string, RouteDefinition>;
let _compiledRoutes: CompiledRoute[];
let _pathnameProvider: IProvidePathName;
let _currentPattern: string | undefined;
let _onPopState: ((event: PopStateEvent) => void) | undefined;

function dispatch(path: string) {
  const [pathname, search] = path.split('?');
  const queryParams = search
    ? (queryString.parse(search) as Params)
    : {};

  const result = matchRoute(pathname, _compiledRoutes);

  if (result) {
    const { route, params } = result;
    const allParams: Params = { ...params, ...queryParams };
    const context: RoutingContext = { params: allParams, path };

    if (_currentPattern && _currentPattern !== route.pattern) {
      const prevRoute = _routes[_currentPattern];
      if (prevRoute && !isCallback(prevRoute)) {
        prevRoute.exit({ params: allParams, path });
      }
    }
    _currentPattern = route.pattern;

    if (isCallback(route.definition)) {
      route.definition(context);
    } else {
      route.definition.start(context);
    }
  } else if (_routes['*']) {
    const wildcard = _routes['*'];
    if (isCallback(wildcard)) {
      wildcard({ params: {}, path });
    }
  }
}

const router: RoutingEffect = {
  initialize(pathnameProvider, routes: Record<string, RouteDefinition>) {
    _routes = routes;
    _pathnameProvider = pathnameProvider;
    _compiledRoutes = Object.entries(routes)
      .filter(([pattern]) => pattern !== '*')
      .map(([pattern, definition]) => {
        const { paramNames, regex } = compileRoute(pattern);
        return { pattern, paramNames, regex, definition };
      });

    _onPopState = () => {
      dispatch(window.location.pathname + window.location.search);
    };
    window.addEventListener('popstate', _onPopState);

    dispatch(window.location.pathname + window.location.search);
  },
  replaceUrl(url: keyof typeof _routes) {
    window.history.replaceState(null, '', url);
    dispatch(url);
  },
  goTo({
    url,
    params = {},
    query
  }: {
    url: string;
    params?: Params;
    query?: Params;
  }) {
    const urlWithReplacedParams = Object.keys(params).reduce(
      (currentUrl, param) => {
        return currentUrl.replace(`:${param}`, params[param]!);
      },
      url
    );
    const stringQuery = query ? '?' + queryString.stringify(query) : '';
    const fullUrl = `${urlWithReplacedParams}${stringQuery}`;

    window.history.pushState(null, '', fullUrl);
    dispatch(fullUrl);
  },
  get tenantId() {
    if (isNil(_pathnameProvider)) {
      return undefined;
    }
    const getQueryParamByKey = (key: string) => {
      const params = new URLSearchParams(location.search);
      return params.get(key) || '';
    };

    const tenantFromQueryParams = getQueryParamByKey('tenantId') as any;
    const urlParts = _pathnameProvider.pathname().split('/');
    const tenantIdFromUrl =
      isNilOrEmpty(tenantFromQueryParams) && urlParts.length > 1
        ? urlParts[1]
        : tenantFromQueryParams;
    if (isValidGuid(tenantIdFromUrl)) return tenantIdFromUrl;

    return undefined;
  }
};

const isNilOrEmpty = either(isEmpty, isNil);

export default router;
